import { useRef, useCallback, useState, useEffect } from "react";
import {
  OrgKey,
  UIThread,
  UIAssistantMessage,
  UIUserMessage,
} from "../../generated/core-api";
import { AsyncResult } from "../../shared";
import { ThreadMessageInput } from "./ThreadMessageInput";
import { motion, AnimatePresence } from "framer-motion";
import { useStreamingCompletion } from "../hooks/useStreamingCompletion";
import { useThread } from "../hooks/useThread";
import s from "./ThreadMessageView.module.css";
import { StructuredOutputView } from "../../structured_output/components";
import { useUserOrgs } from "../../org/hooks";
import { useAuthenticated } from "../../authn";
import { IconButton, Tooltip } from "@datapay-by-datacom/design-system";
import { FiCheck, FiThumbsUp, FiThumbsDown, FiCopy } from "react-icons/fi";

import { useCopyStructuredOutput } from "../../structured_output/hooks/useCopyStructuredOutput";
import { useThreadMessageFeedback } from "../hooks/useThreadMessageFeedback";
import { StreamingProgress } from "./StreamingProgress";
import { SourcePreviewList } from "./SourcePreviewList";

interface ThreadMessageViewProps {
  thread: UIThread;
  setThreadKnowledge: (knowledgeFromOrgs: OrgKey[]) => Promise<void>;
}

export function ThreadMessageView({
  thread,
  setThreadKnowledge,
}: ThreadMessageViewProps) {
  // TODO: If nudge logic grows, extract a useThreadFeedbackNudge(threadId) hook
  // to eliminate prop drilling through ThreadMessageList → AgentMessageView. See PR #249.
  const [showFeedbackNudge, setShowFeedbackNudge] = useState(false);
  const nudgeTimerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    setShowFeedbackNudge(false);
    clearTimeout(nudgeTimerRef.current);
  }, [thread.thread_id]);

  useEffect(() => {
    return () => clearTimeout(nudgeTimerRef.current);
  }, []);

  const handleStreamComplete = useCallback(() => {
    setShowFeedbackNudge(true);
    clearTimeout(nudgeTimerRef.current);
    nudgeTimerRef.current = setTimeout(() => {
      setShowFeedbackNudge(false);
    }, 8000);
  }, []);

  const dismissFeedbackNudge = useCallback(() => {
    setShowFeedbackNudge(false);
    clearTimeout(nudgeTimerRef.current);
  }, []);

  return (
    <div className={s.threadMessageView}>
      <ThreadMessageList
        thread={thread}
        showFeedbackNudge={showFeedbackNudge}
        onNudgeDismiss={dismissFeedbackNudge}
      />
      <ThreadMessageCreate
        thread={thread}
        setThreadKnowledge={setThreadKnowledge}
        onStreamComplete={handleStreamComplete}
        onNudgeDismiss={dismissFeedbackNudge}
      />
    </div>
  );
}

interface ThreadMessageCreateProps {
  thread: UIThread;
  setThreadKnowledge: (knowledgeFromOrgs: OrgKey[]) => Promise<void>;
  onStreamComplete: () => void;
  onNudgeDismiss: () => void;
}

function ThreadMessageCreate({
  thread,
  setThreadKnowledge,
  onStreamComplete,
  onNudgeDismiss,
}: ThreadMessageCreateProps) {
  const { workspace_id: workspaceId, thread_id: threadId } = thread;
  const endRef = useRef<HTMLDivElement>(null);

  // Fetch user orgs for the knowledge selector
  const { userId } = useAuthenticated();
  const userOrgsQuery = useUserOrgs(userId);

  // Get the inferThreadTitle function from useThread hook
  const { inferThreadTitle } = useThread(workspaceId, threadId);

  const streaming = useStreamingCompletion(workspaceId, threadId, endRef, {
    onStart: onNudgeDismiss,
    onComplete: onStreamComplete,
  });

  // Create the wrapper function that handles message submission and title inference
  const handleMessageSubmit = useCallback(
    async (content: string) => {
      const isFirstMessage = thread.messages.length === 0;

      await streaming.streamCompletion(content);

      // If this is the first message, infer the thread title
      if (isFirstMessage) {
        try {
          await inferThreadTitle();
        } catch (error) {
          console.error("Failed to infer thread title:", error);
        }
      }
    },
    [streaming, inferThreadTitle, thread.messages.length]
  );

  return (
    <>
      <AnimatePresence mode="wait">
        {streaming.isStreaming && (
          <>
            <StreamingProgress
              progress={streaming.progress}
              phase={streaming.phase}
              message={streaming.message}
            />
            {streaming.sources.length > 0 && !streaming.result && (
              <SourcePreviewList sources={streaming.sources} />
            )}
          </>
        )}
      </AnimatePresence>

      {streaming.error && (
        <motion.div
          className={s.streamingError}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <span>{streaming.error}</span>
          <button type="button" onClick={streaming.clearError}>Dismiss</button>
        </motion.div>
      )}

      <div className={s.endPadding} />
      <div ref={endRef} />

      <AsyncResult query={userOrgsQuery}>
        {(userOrgs) => (
          <ThreadMessageInput
            handleSubmit={handleMessageSubmit}
            isReceiving={streaming.isStreaming}
            thread={thread}
            userOrgs={userOrgs}
            setThreadKnowledge={setThreadKnowledge}
          />
        )}
      </AsyncResult>
    </>
  );
}

interface ThreadMessageListProps {
  thread: UIThread;
  showFeedbackNudge: boolean;
  onNudgeDismiss: () => void;
}

function ThreadMessageList({
  thread,
  showFeedbackNudge,
  onNudgeDismiss,
}: ThreadMessageListProps) {
  const lastAssistantIndex = thread.messages.reduce<number>(
    (lastIdx, msg, idx) => (msg.role === "assistant" ? idx : lastIdx),
    -1
  );

  return (
    <div className={s.threadMessageList}>
      {thread.messages.map((message, index) => {
        // index should be a stable key because it's an append only list
        switch (message.role) {
          case "user":
            return <UserMessageView message={message} key={index} />;
          case "assistant":
            return (
              <AgentMessageView
                message={message}
                thread={thread}
                messageIndex={index}
                showNudge={showFeedbackNudge && index === lastAssistantIndex}
                onNudgeDismiss={onNudgeDismiss}
                key={index}
              />
            );
        }
      })}
    </div>
  );
}

function UserMessageView({ message }: { message: UIUserMessage }) {
  const userMessageVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };
  return (
    <motion.div
      className={s.userMessageView}
      initial="hidden"
      animate="visible"
      variants={userMessageVariants}
      transition={{ duration: 0.25, ease: "easeOut" }}
    >
      {message.content}
    </motion.div>
  );
}

interface AgentMessageViewProps {
  message: UIAssistantMessage;
  thread: UIThread;
  messageIndex: number;
  showNudge?: boolean;
  onNudgeDismiss?: () => void;
}

function AgentMessageView({
  message,
  thread,
  messageIndex,
  showNudge = false,
  onNudgeDismiss,
}: AgentMessageViewProps) {
  const { isCopied, handleCopy } = useCopyStructuredOutput(message.content);

  const { setFeedback, isSubmitting } = useThreadMessageFeedback(
    thread.workspace_id,
    thread.thread_id,
    messageIndex
  );

  // Get current feedback for this message from the thread's feedback property
  const currentFeedback = thread.feedback?.[messageIndex.toString()];

  const handleThumbsUp = () => {
    const newFeedback = currentFeedback === 1 ? 0 : 1;
    setFeedback(newFeedback);
    if (showNudge) onNudgeDismiss?.();
  };

  const handleThumbsDown = () => {
    const newFeedback = currentFeedback === -1 ? 0 : -1;
    setFeedback(newFeedback);
    if (showNudge) onNudgeDismiss?.();
  };

  return (
    <motion.div
      className={s.agentMessageView}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
    >
      <StructuredOutputView data={message.content} />
      <div className={s.agentMessageFooter}>
        <div className={s.feedbackGroup}>
          <div
            className={`${s.feedbackNudgeBackdrop} ${showNudge ? s.feedbackNudgeBackdropActive : ""}`}
          />

          <Tooltip
            text={
              currentFeedback === 1 ? "Remove good feedback" : "Good response"
            }
            placement="top"
            delay={0}
            closeDelay={300}
          >
            <IconButton
              variant="primary"
              size="small"
              onPress={handleThumbsUp}
              isDisabled={isSubmitting}
              aria-label={currentFeedback === 1 ? "Remove good feedback" : "Good response"}
              className={
                currentFeedback === 1
                  ? s.feedbackButtonActive
                  : showNudge
                    ? s.feedbackButtonNudge
                    : s.feedbackButton
              }
            >
              <FiThumbsUp size={18} />
            </IconButton>
          </Tooltip>

          <Tooltip
            text={
              currentFeedback === -1 ? "Remove bad feedback" : "Bad response"
            }
            placement="top"
            delay={0}
            closeDelay={300}
          >
            <IconButton
              variant="primary"
              size="small"
              onPress={handleThumbsDown}
              isDisabled={isSubmitting}
              aria-label={currentFeedback === -1 ? "Remove bad feedback" : "Bad response"}
              className={
                currentFeedback === -1
                  ? s.feedbackButtonActive
                  : showNudge
                    ? s.feedbackButtonNudge
                    : s.feedbackButton
              }
            >
              <FiThumbsDown size={18} />
            </IconButton>
          </Tooltip>

          <Tooltip
            text={isCopied ? "Copied!" : "Copy message"}
            placement="top"
            delay={0}
            closeDelay={300}
          >
            <IconButton
              variant="primary"
              size="small"
              onPress={handleCopy}
              aria-label={isCopied ? "Copied" : "Copy message"}
              className={s.feedbackButton}
            >
              {isCopied ? <FiCheck size={18} /> : <FiCopy size={18} />}
            </IconButton>
          </Tooltip>

          <span
            className={`${s.feedbackNudgeLabel} ${showNudge ? s.feedbackNudgeLabelActive : ""}`}
          >
            Was this helpful?
          </span>
        </div>
      </div>
    </motion.div>
  );
}
